<template>
  <v-text-field
    v-if="
      filterSelectionConfiguration &&
      (!filterSelectionConfiguration.items || !filterSelectionConfiguration.items.length)
    "
    v-model="input"
    :type="filterSelectionConfiguration.type"
    :rules="filterSelectionConfiguration.rules"
    :placeholder="filterSelectionConfiguration.placeholder"
    hide-details
    :outlined="outlined"
    :dense="dense"
    data-test-filter-value
  />
  <v-autocomplete
    v-else-if="
      filterSelectionConfiguration && filterSelectionConfiguration.items && !filterSelectionConfiguration.component
    "
    v-model="input"
    :rules="filterSelectionConfiguration.rules"
    :placeholder="filterSelectionConfiguration.placeholder"
    :items="filterSelectionConfiguration.items"
    :item-value="filterSelectionConfiguration.itemValue"
    :item-text="filterSelectionConfiguration.itemText"
    hide-details
    :outlined="outlined"
    :dense="dense"
  >
    <template #append> <div style="display: none" /> </template>
    <template #item="{ item }"> {{ item.text ? $t(item.text) : item }} </template>
    <template #selection="{ item }"> {{ item.text ? $t(item.text) : item }} </template>
  </v-autocomplete>

  <v-autocomplete
    v-else-if="filterSelectionConfiguration && filterSelectionConfiguration.component"
    v-model="input"
    :rules="filterSelectionConfiguration.rules"
    :placeholder="filterSelectionConfiguration.placeholder"
    :items="filterSelectionConfiguration.items"
    :item-value="filterSelectionConfiguration.itemValue"
    :item-text="filterSelectionConfiguration.itemText"
    hide-details
    :outlined="outlined"
    :dense="dense"
  >
    <template #item="{ item }">
      <component
        v-bind:is="filterSelectionConfiguration.component"
        v-bind="
          filterSelectionConfiguration.mapItemToComponent ? filterSelectionConfiguration.mapItemToComponent(item) : item
        "
        :small="true"
      />
    </template>
    <template #selection="{ item }">
      <component
        v-bind:is="filterSelectionConfiguration.component"
        v-bind="
          filterSelectionConfiguration.mapItemToComponent ? filterSelectionConfiguration.mapItemToComponent(item) : item
        "
        :small="true"
      />
    </template>
  </v-autocomplete>
</template>

<script lang="ts">
import FilterSelection from "@/components/filter/FilterSelection.vue";
import { requiredRule } from "@/lib/rules/requiredRule";
import { PageFilterTypes } from "@/lib/utility/data/page-filter-types.enum";
import { PaginationFilterListElement } from "@/store/modules/base-pagination.store";
import { Component, Prop, Vue } from "vue-property-decorator";
import CustomFieldValueChip from "../report/CustomFieldValueChip.vue";
import RefsCostGroup from "../utility/RefsCostGroup.vue";
import RefsProject from "../utility/RefsProject.vue";
import RefsUser from "../utility/RefsUser.vue";
import RefsVehicle from "../utility/RefsVehicle.vue";
import RefsHandover from "../utility/RefsHandover.vue";
import RefsSnapshot from "../utility/RefsSnapshot.vue";
import RefsDocument from "../utility/RefsDocument.vue";
import RefsCompany from "../utility/RefsCompany.vue";
import RefsPartner from "../utility/RefsPartner.vue";
import RefsPerson from "../utility/RefsPerson.vue";
import RefsPersonGroup from "../utility/RefsPersonGroup.vue";
import RefsCompanyGroup from "../utility/RefsCompanyGroup.vue";
import RefsContract from "../utility/RefsContract.vue";
import RefsGroup from "../utility/RefsGroup.vue";
import RefsFleet from "../utility/RefsFleet.vue";
import RefsReport from "../utility/RefsReport.vue";
import RefsCustomerAccount from "../utility/RefsCustomerAccount.vue";
import RefsEvent from "../utility/RefsEvent.vue";
import RefsLeadGroup from "../utility/RefsLeadGroup.vue";
import RefsPartnerBanking from "../utility/RefsPartnerBanking.vue";
import RefsRepairEstimate from "../utility/RefsRepairEstimate.vue";
import RefsReportGroup from "../utility/RefsReportGroup.vue";
import TagChip from "../tag/TagChip.vue";
// import RefsTicket from "../utility/RefsTicket.vue";
import { $t } from "@/lib/utility/t";

@Component({
  components: {
    FilterSelection,
    RefsCostGroup,
    RefsUser,
    RefsProject,
    RefsHandover,
    RefsSnapshot,
    // RefsTicket,
    CustomFieldValueChip,
    RefsDocument,
    RefsCompany,
    RefsPartner,
    RefsCompanyGroup,
    RefsPerson,
    RefsPersonGroup,
    RefsContract,
    RefsGroup,
    RefsVehicle,
    RefsFleet,
    RefsReport,
    RefsCustomerAccount,
    RefsEvent,
    RefsLeadGroup,
    RefsPartnerBanking,
    RefsRepairEstimate,
    RefsReportGroup,
    TagChip
  }
})
export default class FilterCardPaginationValueField extends Vue {
  @Prop()
  value!: string;

  @Prop()
  filterConfiguration!: PaginationFilterListElement;

  @Prop({ default: false })
  dense!: boolean;

  @Prop({ default: false })
  outlined!: boolean;

  @Prop({ default: false })
  supressRequired!: boolean;

  getFilterConfigurationItems(filterConfiguration: PaginationFilterListElement) {
    const itemCallback = filterConfiguration?.config?.itemCallback;
    if (itemCallback) {
      return itemCallback();
    }

    return filterConfiguration?.config?.items;
  }

  get required() {
    const rules = [];
    if (!this.supressRequired) {
      rules.push(requiredRule());
    }

    return rules;
  }
  get booleanRules() {
    return [(v: boolean) => v === true || v === false || "Must be defined"];
  }
  get objectIdRules() {
    const rules = [];
    if (!this.supressRequired) {
      rules.push(requiredRule());
    }
    rules.push((v: string) => v.length === 24 || "ObjectIds must be 24 characters long");

    return rules;
  }

  /**
   * Get the configuration of the input/selection field based on the given filter
   */
  get filterSelectionConfiguration() {
    const filterConfiguration = this.filterConfiguration;

    if (!filterConfiguration) {
      return undefined;
    }

    /**
     * The type of the input field
     */
    const typeMap = new Map([
      [PageFilterTypes.STRING, undefined],
      [PageFilterTypes.NUMBER, "number"],
      [PageFilterTypes.OBJECT_ID, undefined],
      [PageFilterTypes.BOOLEAN, undefined],
      [PageFilterTypes.ENUM, undefined],
      [PageFilterTypes.DATE, "date"],
      [PageFilterTypes.CUSTOM_FIELD_DATE_VALUE, "date"],
      [PageFilterTypes.CUSTOM_FIELD_NUMBER_VALUE, "number"],
      [PageFilterTypes.CUSTOM_FIELD_VALUE, undefined]
    ]);

    /**
     * The validation of the input
     */
    const rulesMap = new Map([
      [PageFilterTypes.STRING, this.required],
      [PageFilterTypes.NUMBER, this.required],
      [PageFilterTypes.OBJECT_ID, this.objectIdRules],
      [PageFilterTypes.BOOLEAN, this.booleanRules],
      [PageFilterTypes.ENUM, this.required],
      [PageFilterTypes.DATE, this.required]
    ]);

    /**
     * The placeholder of the input
     */
    const placeholderMap = new Map([
      [PageFilterTypes.STRING, "Value"],
      [PageFilterTypes.NUMBER, "Value"],
      [PageFilterTypes.OBJECT_ID, "61e9880bb4c41b6269ef5649"],
      [PageFilterTypes.BOOLEAN, this.$t("yes") + "/" + this.$t("no")],
      [PageFilterTypes.ENUM, this.getFirstItemFromConfigItems(filterConfiguration)],
      [PageFilterTypes.DATE, "2000-12-31"]
    ]);

    /**
     * The configuration of the input
     */
    return {
      key: filterConfiguration.key,
      type: typeMap.get(filterConfiguration.type),
      items:
        filterConfiguration?.type === PageFilterTypes.BOOLEAN
          ? [
              { value: true, text: $t("yes") },
              { value: false, text: $t("no") }
            ]
          : this.getFilterConfigurationItems(this.filterConfiguration),
      component: filterConfiguration.config?.component,
      rules: rulesMap.get(filterConfiguration.type),
      placeholder: placeholderMap.get(filterConfiguration.type),
      itemValue: filterConfiguration.config?.itemValue,
      itemText: filterConfiguration.config?.itemText,
      mapItemToComponent: filterConfiguration.config?.mapItemToComponent
    };
  }

  readonly FilterTypes = PageFilterTypes;

  get input(): string {
    return this.value;
  }
  set input(v: string) {
    this.$emit("input", v);
  }

  private getFirstItemFromConfigItems(filterConfiguration: PaginationFilterListElement) {
    const items = this.getFilterConfigurationItems(filterConfiguration);
    if (!items?.length) {
      return "";
    }

    if (typeof items[0] === "string") {
      return items[0];
    } else {
      return "";
    }
  }
}
</script>
